import type {GenplanBuilding} from '../index';
import markerInfoTemplate from './genplan-marker-info.twig';
import markerTemplate from './genplan-marker.twig';
import {Utils} from '@/common/scripts/utils';

/**
 * Класс для управления зданиями на генплане
 */
export class GenplanBuildings {
    private readonly wrapper: HTMLElement;
    private readonly mapImgWrapper: HTMLElement;
    private readonly buildings: GenplanBuilding[];

    private currentPanel: HTMLElement | null = null;
    private hideTimeout: number | null = null;

    private onGoToRoomsCallback: ((building: GenplanBuilding) => void) | null = null;

    constructor(wrapper: HTMLElement, buildings: GenplanBuilding[]) {
        this.wrapper = wrapper;
        this.mapImgWrapper = wrapper.querySelector<HTMLElement>('.j-genplan-map-img-wrapper')!;
        this.buildings = buildings;

        this.renderMarkers();
    }


    public setOnGoToRoomsCallback(callback: (building: GenplanBuilding) => void): void {
        this.onGoToRoomsCallback = callback;
    }

    /**
     * Рендерит маркеры зданий на карте
     */
    private renderMarkers(): void {
        this.buildings.forEach((building: GenplanBuilding) => {
            const markerHtml = markerTemplate(building);

            this.mapImgWrapper.insertAdjacentHTML('beforeend', markerHtml);
        });

        this.bindMarkerEvents();
    }

    /**
     * Привязывает обработчики событий к маркерам зданий
     */
    private bindMarkerEvents(): void {
        const markers = this.mapImgWrapper.querySelectorAll<HTMLElement>('.j-genplan-marker');

        markers.forEach((marker: HTMLElement) => {
            marker.addEventListener('mouseenter', this.onMouseEnterMarker.bind(this));
            marker.addEventListener('mouseleave', this.scheduleHidePanel.bind(this));
            marker.addEventListener('click', this.onMouseEnterMarker.bind(this));
        });
    }

    /**
     * Обработчик наведения мыши на маркер здания
     * @param {MouseEvent} event - Событие мыши
     */
    private onMouseEnterMarker(event: MouseEvent): void {
        const markerEl = event.currentTarget as HTMLElement;
        const buildingId = markerEl.dataset['id'];
        const building = this.findBuildingById(buildingId);

        if (!building) {
            return;
        }

        this.cancelHideTimeout();
        this.removePanel();
        this.createPanel(building, markerEl);
    }

    /**
     * Находит здание по ID
     * @param {string | undefined} buildingId - ID здания
     * @returns {GenplanBuilding | undefined} - Здание или undefined, если не найдено
     */
    private findBuildingById(buildingId?: string): GenplanBuilding | undefined {
        return this.buildings.find((b: GenplanBuilding) => {
            return b.id === buildingId;
        });
    }

    /**
     * Создаёт информационную панель маркера
     * @param {GenplanBuilding} building - Данные о здании
     * @param {HTMLElement} markerEl - Элемент маркера
     */
    private createPanel(building: GenplanBuilding, markerEl: HTMLElement): void {
        const infoPanelHtml = markerInfoTemplate(building);

        this.wrapper.insertAdjacentHTML('beforeend', infoPanelHtml);

        this.currentPanel = this.wrapper.querySelector<HTMLElement>('.j-genplan-marker-info');

        if (this.currentPanel) {
            this.positionPanel(markerEl, this.currentPanel);
            this.bindPanelEvents(building);
        }
    }

    /**
     * Позиционирует информационную панель рядом с маркером
     * @param {HTMLElement} markerEl - Элемент маркера
     * @param {HTMLElement} infoPanel - Элемент информационной панели
     */
    private positionPanel(markerEl: HTMLElement, infoPanel: HTMLElement): void {
        if (Utils.isMobile()) {
            infoPanel.style.left = `0px`;
            infoPanel.style.bottom = `0px`;

            return;
        }

        const markerRect = markerEl.getBoundingClientRect();
        const wrapperRect = this.wrapper.getBoundingClientRect();
        const panelRect = infoPanel.getBoundingClientRect();
        const offset = 20;

        let left = markerRect.left - wrapperRect.left + markerRect.width + offset;
        let top = markerRect.top - wrapperRect.top + (markerRect.height / 2) - (panelRect.height / 2);

        // Позиционируем справа или слева от маркера
        if (left + panelRect.width > wrapperRect.width) {
            left = markerRect.left - wrapperRect.left - panelRect.width - offset;
        }

        // Корректируем границы
        left = Math.max(offset, Math.min(left, wrapperRect.width - panelRect.width - offset));
        top = Math.max(offset, Math.min(top, wrapperRect.height - panelRect.height - offset));

        infoPanel.style.left = `${left}px`;
        infoPanel.style.top = `${top}px`;
    }

    /**
     * Привязывает обработчики событий к информационной панели маркера
     * @param {GenplanBuilding} building - Данные о здании
     */
    private bindPanelEvents(building: GenplanBuilding): void {
        if (!this.currentPanel) {
            return;
        }

        this.currentPanel.addEventListener('mouseenter', this.cancelHideTimeout.bind(this));

        if (!Utils.isMobile()) {
            this.currentPanel.addEventListener('mouseleave', this.scheduleHidePanel.bind(this));
        }

        const goToRoomsBtn = this.currentPanel.querySelector<HTMLElement>('.j-genplan-go-to-room-btn');

        if (goToRoomsBtn && this.onGoToRoomsCallback) {
            goToRoomsBtn.addEventListener('click', () => {
                this.onGoToRoomsCallback!(building);
                this.removePanel();
            });
        }

        const closePanelBtn = this.currentPanel.querySelector<HTMLElement>('.j-genplan-marker-info-close-btn');

        if (closePanelBtn) {
            closePanelBtn.addEventListener('click', this.removePanel.bind(this));
        }
    }

    /**
     * Запускает таймер на скрытие информационной панели маркера
     */
    private scheduleHidePanel(): void {
        this.cancelHideTimeout();
        this.hideTimeout = window.setTimeout(() => {
            this.removePanel();
        }, 100);
    }

    /**
     * Отменяет запланированное скрытие информационной панели
     */
    private cancelHideTimeout(): void {
        if (this.hideTimeout !== null) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
    }

    /**
     * Удаляет информационную панель маркера
     */
    private removePanel(): void {
        if (this.currentPanel) {
            this.currentPanel.remove();
            this.currentPanel = null;
        }
    }
}
